import {API_MODULES, API_ROUTES, API_SOLUTIONS} from "@api/ApiRoutes";
import {
    CONTENTIO_ACCESS_MODIFIER,
    CONTENTIO_MODULE_ENUM,
    DEFAULT_CONTENTIO_MODULENAME,
    UploadParameters,
    UploadResult
} from "@lib/ContentIo/ContentIoModels";
import {getRequest, postRequest} from "@api/ApiService";
import {generateApiEndpoint} from "@api/ApiUtils";

const MAX_FILES_PER_REQUEST = 5;

function chunkArray<T>(arr: T[], size: number): T[][] {
    if (size <= 0) return [arr];
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

const ROUTES = {
    UPLOAD: {
        path: API_ROUTES.UPLOAD_FILE,
        module: API_MODULES.CORE_CONTENT_IO,
        solution: API_SOLUTIONS.MZ
    },
    UPLOAD_ZENDESK: {
        path: API_ROUTES.ZENDESK_UPLOAD_FILE,
        module: API_MODULES.CORE_CONTENT_IO,
        solution: API_SOLUTIONS.MZ
    },
    DOWNLOAD_PRIVATE: {
        path: API_ROUTES.DOWNLOAD_FILE,
        module: API_MODULES.CORE_CONTENT_IO,
        solution: API_SOLUTIONS.MZ
    },
    DOWNLOAD_PUBLIC: {
        path: API_ROUTES.DOWNLOAD_FILE,
        module: API_MODULES.CORE_CONTENT_IO,
        solution: API_SOLUTIONS.MZ
    }
}

class ContentIoService {

    static upload = async (file: File, parameters?: UploadParameters): Promise<UploadResult[]> => {

        const {
            moduleName = DEFAULT_CONTENTIO_MODULENAME,
            middlePath = undefined,
            subDir = undefined,
            accessModifier = CONTENTIO_ACCESS_MODIFIER.PRIVATE
        } = parameters || {};

        const urlParameters = parameters?.destination === "zendesk" ? ROUTES.UPLOAD_ZENDESK : ROUTES.UPLOAD;

        const formData = new FormData();

        const suffix = `${Date.now()}_0`;
        formData.append("files", this.buildUniqueFile(file, suffix));

        return await postRequest<UploadResult[], FormData>(generateApiEndpoint(urlParameters), formData, {
            params: {
                moduleName,
                middlePath,
                subDir,
                accessModifier
            },
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
    }

    static manyUpload = async (payload: File[], parameters?: UploadParameters): Promise<UploadResult[]> => {
        const {
            moduleName = CONTENTIO_MODULE_ENUM.JNEXT,
            middlePath = undefined,
            subDir = undefined,
            accessModifier = CONTENTIO_ACCESS_MODIFIER.PRIVATE
        } = parameters || {};

        const chunks = chunkArray(payload, MAX_FILES_PER_REQUEST);

        const allResults: UploadResult[] = [];

        const urlParameters = parameters?.destination === "zendesk" ? ROUTES.UPLOAD_ZENDESK : ROUTES.UPLOAD;

        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            const filesChunk = chunks[chunkIndex];

            const formData = new FormData();

            filesChunk.forEach((file, idx) => {
                const suffix = `${Date.now()}_${idx}`;
                formData.append("files", this.buildUniqueFile(file, suffix));
            });

            const res = await postRequest<UploadResult[], FormData>(
                generateApiEndpoint(urlParameters),
                formData,
                {
                    params: {moduleName, middlePath, subDir, accessModifier},
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            allResults.push(...res);
        }

        return allResults;
    }
    static downloadFile = async (fileName: string, accessToken?: string, accessType: "private" | "public" = "private"): Promise<{
        blobData: any;
        contentDisposition: string;
    }> => {

        const url = generateApiEndpoint(accessType === "private" ? ROUTES.DOWNLOAD_PRIVATE : ROUTES.DOWNLOAD_PUBLIC);

        const authHeader = !!accessToken ? {Authorization: `Bearer ${accessToken}`} : {};

        const {data, headers} = await getRequest<any>(url, {
            params: {
                filename: fileName
            },
            headers: {
                accept: "application/octet-stream",
                ...authHeader
            },
            responseType: "blob"

        }, true);

        return {
            blobData: data,
            contentDisposition: headers["content-disposition"] ?? ""
        }
    }

    private static buildUniqueFile(file: File, uniqueSuffix: string) {
        const lastDot = file.name.lastIndexOf(".");
        const base = lastDot > 0 ? file.name.slice(0, lastDot) : file.name;
        const ext = lastDot > 0 ? file.name.slice(lastDot + 1) : "";
        const fileName = ext ? `${base}_${uniqueSuffix}.${ext}` : `${base}_${uniqueSuffix}`;
        return new File([file], fileName, {type: file.type});
    }

}

export default ContentIoService;